import { useCallback, useEffect, useState } from 'react'
import firebase from '../firebase/compat'
import { client } from './Client'

// De inzendingen via een storytelling-deellink die nog wachten op
// beoordeling. Via een callable: de wachtrij is server-only (ADR-035).
// Eén keer opgehaald op de storytellingpagina, voor de teller in de tab én
// voor de tab zelf.
// AI-CONTEXT: See docs/features/storytelling-deellink.md
// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading

/**
 * @typedef {object} StorytellingSubmissionRow
 * @property {string} id
 * @property {'media'|'quote'|'anecdote'} kind
 * @property {string} text
 * @property {string} name
 * @property {string} contentType
 * @property {number | null} size
 * @property {Array<string>} activity
 * @property {number | null} created - epoch-ms
 * @property {string | null} previewUrl - Leeslink van een uur (alleen media)
 */

/**
 * @returns {{submissions: Array<StorytellingSubmissionRow> | null, failed: boolean, reload: () => Promise<void>, remove: (id: string) => void}}
 *   `submissions` is null zolang het niet bekend is (laden of mislukt). `remove`
 *   haalt één beoordeelde inzending uit de lijst zonder nieuwe aanroep: een
 *   nieuwe lijst zou elke foto een nieuwe leeslink geven, en de browser laadt
 *   ze dan allemaal opnieuw.
 */
const useStorytellingSubmissions = () => {
    const [submissions, setSubmissions] = useState(/** @type {Array<StorytellingSubmissionRow> | null} */ (null))
    const [failed, setFailed] = useState(false)

    const reload = useCallback(async () => {
        try {
            const { data } = await firebase.app().functions('europe-west1').httpsCallable('listStorytellingSubmissions')({ client })
            setSubmissions(data.submissions)
            setFailed(false)
        } catch (error) {
            console.error('useStorytellingSubmissions: laden mislukt', error)
            setFailed(true)
        }
    }, [])

    useEffect(() => {
        reload()
    }, [reload])

    const remove = useCallback((id) => {
        setSubmissions((prev) => (Array.isArray(prev) ? prev.filter((s) => s.id !== id) : prev))
    }, [])

    return { submissions, failed, reload, remove }
}

export default useStorytellingSubmissions
